import { useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";

export default function ChangePassword({ forced = false }) {
  const { user, changePassword, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 6) return setError("Le nouveau mot de passe doit contenir au moins 6 caractères.");
    if (next !== confirm) return setError("Les mots de passe ne correspondent pas.");
    setBusy(true);
    try {
      await changePassword(current, next);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen app-bg flex items-center justify-center p-6" data-testid="change-password-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md glass rounded-2xl p-8 card-3d">
        <div className="h-14 w-14 rounded-2xl flex items-center justify-center mb-5" style={{ background: "linear-gradient(135deg,#6366f1,#ec4899)" }}>
          <KeyRound className="h-7 w-7 text-white" />
        </div>
        <h1 className="font-display text-2xl font-extrabold">{forced ? "Définissez votre mot de passe" : "Changer le mot de passe"}</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          {forced
            ? `Bienvenue ${user?.name?.split(" ").slice(-1)[0] || ""}. Pour des raisons de sécurité, choisissez un nouveau mot de passe personnel.`
            : "Mettez à jour votre mot de passe."}
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="space-y-2">
            <Label>{forced ? "Mot de passe temporaire" : "Mot de passe actuel"}</Label>
            <Input type="password" data-testid="cp-current" value={current} onChange={(e) => setCurrent(e.target.value)} className="h-12" required />
          </div>
          <div className="space-y-2">
            <Label>Nouveau mot de passe</Label>
            <Input type="password" data-testid="cp-new" value={next} onChange={(e) => setNext(e.target.value)} className="h-12" required />
          </div>
          <div className="space-y-2">
            <Label>Confirmer</Label>
            <Input type="password" data-testid="cp-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-12" required />
          </div>

          {error && <div data-testid="cp-error" className="text-sm text-red-200 bg-red-500/15 border border-red-500/30 rounded-xl px-4 py-3">{error}</div>}

          <Button type="submit" data-testid="cp-submit" disabled={busy} className="w-full h-12 font-semibold">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Enregistrer le mot de passe"}
          </Button>
        </form>

        <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Min. 6 caractères</span>
          <button onClick={logout} className="hover:text-foreground transition" data-testid="cp-logout">Se déconnecter</button>
        </div>
      </motion.div>
    </div>
  );
}
